/**
 * client/src/components/ui/metric-tooltip.tsx
 *
 * The canonical Radix-based metric/badge tooltip wrapper for the conversation-thread
 * screen (Comms Conversation-Thread, Task 11 / AC-16). DESIGN-SYSTEM §3.10 mandates
 * that EVERY status badge + numeric/abbreviated metric carry a plain-language tooltip,
 * delivered as: desktop hover after 300ms, mobile long-press, available to screen
 * readers via a persistent `role="tooltip"` element that
 * the trigger references with `aria-describedby` (§6.4).
 *
 * Reuses `@radix-ui/react-tooltip` — the project's canonical tooltip primitive (the
 * same pattern as dashboard/components/kpi-card.tsx). The trigger child is cloned so
 * the wrapper can inject the matching `aria-describedby` without an extra wrapper node
 * (badges must stay inline). A separate aria-hidden Radix layer owns only the
 * visual disclosure, avoiding a second assistive-technology announcement.
 */

import * as Tooltip from "@radix-ui/react-tooltip";
import React, { useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/cn";

interface MetricTooltipProps {
  /** The plain-language definition (non-technical org admin; §3.10). */
  readonly content: React.ReactNode;
  /**
   * The trigger element (the badge / metric). It is cloned with `aria-describedby`
   * pointing at the tooltip content, so the badge text stays the accessible name and
   * the definition is the description.
   */
  readonly children: React.ReactElement<{ "aria-describedby"?: string }>;
}

const TOUCH_LONG_PRESS_MS = 500;

/**
 * Wrap a badge/metric with one persistent linked definition plus visual disclosure.
 * Radix owns desktop hover/focus; a controlled 500ms timer owns touch disclosure.
 */
export function MetricTooltip({ content, children }: MetricTooltipProps): React.ReactElement {
  const descriptionId = useId();
  const [open, setOpen] = useState(false);
  const longPressTimer = useRef<number | null>(null);
  const releaseTimer = useRef<number | null>(null);
  const suppressRadixClose = useRef(false);
  const existingDescribedBy = children.props["aria-describedby"];
  const describedBy = [existingDescribedBy, descriptionId].filter(Boolean).join(" ");

  const clearLongPress = (): void => {
    if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };

  const clearRelease = (): void => {
    if (releaseTimer.current !== null) window.clearTimeout(releaseTimer.current);
    releaseTimer.current = null;
  };

  useEffect(() => () => {
    clearLongPress();
    clearRelease();
  }, []);

  const handlePointerDown = (event: React.PointerEvent): void => {
    if (event.pointerType !== "touch") return;
    clearLongPress();
    clearRelease();
    longPressTimer.current = window.setTimeout(() => {
      longPressTimer.current = null;
      suppressRadixClose.current = true;
      setOpen(true);
    }, TOUCH_LONG_PRESS_MS);
  };

  const handlePointerMove = (event: React.PointerEvent): void => {
    if (event.pointerType !== "touch") return;
    clearLongPress();
    clearRelease();
    if (suppressRadixClose.current) {
      suppressRadixClose.current = false;
      setOpen(false);
    }
  };

  const handlePointerCancel = (event: React.PointerEvent): void => {
    if (event.pointerType !== "touch") return;
    clearLongPress();
    clearRelease();
    suppressRadixClose.current = false;
    setOpen(false);
  };

  const handlePointerUp = (event: React.PointerEvent): void => {
    if (event.pointerType !== "touch") return;
    clearLongPress();
    if (!suppressRadixClose.current) return;
    clearRelease();
    releaseTimer.current = window.setTimeout(() => {
      suppressRadixClose.current = false;
      releaseTimer.current = null;
    }, 0);
  };

  return (
    <Tooltip.Provider delayDuration={300}>
      <Tooltip.Root
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && suppressRadixClose.current) return;
          setOpen(nextOpen);
        }}
      >
        <Tooltip.Trigger
          asChild
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
        >
          {React.cloneElement(children, { "aria-describedby": describedBy })}
        </Tooltip.Trigger>
        <span id={descriptionId} role="tooltip" className="sr-only">
          {content}
        </span>
        <Tooltip.Portal>
          <Tooltip.Content
            aria-hidden="true"
            sideOffset={6}
            className={cn(
              "z-50 max-w-[240px] rounded-[var(--radius-md)] border border-border-default",
              "bg-bg-overlay px-3 py-2 text-[length:var(--text-caption-size)] leading-[1.4] text-text-primary",
              "shadow-elevation-2",
            )}
          >
            {content}
            <Tooltip.Arrow className="fill-bg-overlay" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
